/**
 * ログインのトークンを受け取ったときの共通処理（複数の会社に所属するアカウントのフェーズ 2・2026-10-17）。
 * 設計: docs/04_機能仕様/複数の会社に所属するアカウント設計.md §9.1
 *
 * 以前は 5 か所（AuthContext の login と setupComplete・LINE のコールバック・SetupForm の 2 か所）が
 * それぞれ localStorage・cookie・axios の既定ヘッダを書いていて、LINE のコールバックだけ保存値を消さないなど、ずれていた。
 * ここで 1 つにまとめ、トークンと一緒に「今の会社」（タブのメモリ）と「選んでいる店」を同じ場所で書く。
 */
import axios from './axios';
import {
  CompanyMembership,
  keepSelectedStore,
  lastStoreKey,
  LAST_STORE_KEY_PREFIX,
  setCurrentCompanyId,
  setMemberships,
} from './companyContext';

export interface AcceptAuthTokenParams {
  token: string;
  currentCompanyId?: number | null;
  companies?: CompanyMembership[] | null;
  /** 店を選んだ（切り替え・店選びの画面）。トークンと一緒に書く */
  selectedStoreId?: number | null;
  userId?: number | null;
}

export function acceptAuthToken(p: AcceptAuthTokenParams): void {
  if (typeof window === 'undefined' || !p.token) return;

  try { localStorage.setItem('auth_token', p.token); } catch { /* 下の cookie と既定ヘッダで続ける */ }
  // middleware（Next.js）がページの保護に使うので cookie にも書く
  document.cookie = `auth_token=${p.token}; path=/; max-age=86400; SameSite=Lax`;
  axios.defaults.headers.common['Authorization'] = `Bearer ${p.token}`;

  if (p.currentCompanyId !== undefined) setCurrentCompanyId(p.currentCompanyId);
  if (p.companies) setMemberships(p.companies);

  try {
    if (p.selectedStoreId) {
      // 🔴 店を選んだときは、トークンと一緒に書く（同じ同期処理の中で）。
      //    書かずに消すと StoreContext が先頭の店を自動で選ぶので、2 号店を選んだのに本店で出勤する
      localStorage.setItem('selected_store_id', String(p.selectedStoreId));
      (window as any).__logmeSelectedStoreId = p.selectedStoreId;
      rememberLastStore(p.userId, p.selectedStoreId, p.currentCompanyId ?? null);
    } else if (!keepSelectedStore(localStorage.getItem('selected_store_id'), p.currentCompanyId ?? null, p.companies ?? [])) {
      // 今の会社で入れる店に無い保存値は消す（別の人・別の会社で入った端末に古い店が残ると、全部の要求が 409 になる）
      localStorage.removeItem('selected_store_id');
      (window as any).__logmeSelectedStoreId = undefined;
    }
  } catch { /* 保存値を扱えなくてもログインは続ける */ }
}

/** 同じ会社の店を選んだ（トークンは替えない）。選んでいる店と、その端末で前回選んだ店を書く */
export function writeSelectedStore(storeId: number, userId: number | string | null | undefined, companyId: number | null): void {
  try {
    localStorage.setItem('selected_store_id', String(storeId));
    (window as any).__logmeSelectedStoreId = storeId;
  } catch { /* 書けなくても StoreContext が選び直す */ }
  rememberLastStore(userId, storeId, companyId);
}

/**
 * その端末で前回選んだ店を覚える（店舗セレクタ・店選びの画面で選んだとき）。
 * 会社をまたいだ前回の店（店選びの並び）と、その会社での前回の店（店の 409 から戻す先）の 2 つを書く
 */
export function rememberLastStore(
  userId: number | string | null | undefined,
  storeId: number | string | null | undefined,
  companyId?: number | string | null,
): void {
  if (!userId || !storeId) return;
  try {
    localStorage.setItem(lastStoreKey(userId), String(storeId));
    if (companyId) localStorage.setItem(lastStoreKey(userId, companyId), String(storeId));
  } catch { /* 覚えられなくても動く */ }
}

export function readLastStore(userId: number | string | null | undefined): number | null {
  if (!userId) return null;
  try {
    const v = Number(localStorage.getItem(lastStoreKey(userId)));
    return Number.isInteger(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/**
 * localStorage を全部消す。ただし「その端末で前回選んだ店」（ユーザーごと）は残す。
 * 消えると、共用タブレットで次にログインしたときの店選びの並びが崩れる（取り違えを防げない）
 */
export function clearLocalStorageKeepingLastStores(): void {
  try {
    const keep: [string, string][] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(LAST_STORE_KEY_PREFIX)) {
        const value = localStorage.getItem(key);
        if (value !== null) keep.push([key, value]);
      }
    }
    localStorage.clear();
    keep.forEach(([k, v]) => localStorage.setItem(k, v));
  } catch { /* 消せなくても続ける */ }
}
